import { useState } from "react";
import { InstrumentBar, Telemetry, type Readout } from "./shell";
import { useReducedMotion, useSweep, type NonEmpty } from "./useInstrument";

/* =====================================================================
   Facility pack readout — the hero figure.

   Answers one question: "what runs at MY facility?" Picking a type shows
   the pack that gets assigned and what it cites.

   The Custom tab is the one to be careful with. The product does NOT let a
   customer author rules or bring their own citable sources — `custom` is a
   facility_type that simply gets NO auto-assignment, so an admin picks
   packs from the shipped catalog instead (see the comment on
   defaultPackKeyForFacilityType in apps/api). The copy here says exactly
   that and nothing more.
   ===================================================================== */

type PackKey = "snf" | "alf" | "asc" | "dialysis" | "custom";

interface Pack {
  key: PackKey;
  eyebrow: string;
  name: string;
  /** Heading over the coverage list. */
  covTitle: string;
  /** Right-hand count on the coverage header. */
  covCount: string;
  /** [name, what it governs, is it something the admin chose] */
  standards: readonly [string, string, boolean][];
  note: string;
  tele: readonly Readout[];
  findingKicker: string;
  finding: { title: string; meta: string; severity: string; tone: "crit" | "warn"; cites: readonly string[] };
  footLeft: string;
  footRight: string;
}

const LTC_STANDARDS: readonly [string, string, boolean][] = [
  ["ASCP standards of practice", "Consultant pharmacist practice", false],
  ["Beers 2023", "Potentially inappropriate medications", false],
  ["STOPP/START v2", "Prescribing omissions and excess", false],
  ["42 CFR §483.45", "F605 · F757 · F881", false],
  ["Boustani ACB", "Anticholinergic burden scoring", false],
];

const PACKS: NonEmpty<Pack> = [
  {
    key: "snf",
    eyebrow: "Skilled nursing",
    name: "SNF",
    covTitle: "Standards in the long-term care pack",
    covCount: "5 cited",
    standards: LTC_STANDARDS,
    note: "The long-term care pack is assigned automatically when the facility is created. An admin can swap it from the catalog.",
    tele: [
      { value: "5", label: "Standards cited" },
      { value: "Roster", label: "Reviewed by resident" },
      { value: "100%", label: "Findings cited" },
    ],
    findingKicker: "What it catches",
    finding: {
      title: "Anticholinergic burden ≥ 3 with concurrent fall risk",
      meta: "4 contributing agents · resident with a documented fall in 90 days",
      severity: "Critical",
      tone: "crit",
      cites: ["Beers 2023 · Table 8", "F605 · §483.45(e)", "Boustani ACB 3"],
    },
    footLeft: "Assigned automatically at facility creation",
    footRight: "18 flags · 18 cited",
  },
  {
    key: "alf",
    eyebrow: "Assisted living",
    name: "ALF",
    covTitle: "Standards in the long-term care pack",
    covCount: "5 cited",
    standards: LTC_STANDARDS,
    note: "Assisted living inherits the long-term care pack as the closest fit — the geriatric and polypharmacy overlap is real. Swap it if you disagree.",
    tele: [
      { value: "5", label: "Standards cited" },
      { value: "Roster", label: "Reviewed by resident" },
      { value: "100%", label: "Findings cited" },
    ],
    findingKicker: "What it catches",
    finding: {
      title: "Anticholinergic burden ≥ 3 with concurrent fall risk",
      meta: "Geriatric and polypharmacy overlap with skilled nursing",
      severity: "Critical",
      tone: "crit",
      cites: ["Beers 2023 · Table 8", "Boustani ACB 3"],
    },
    footLeft: "Inherits the long-term care pack — swappable",
    footRight: "Swappable by an admin",
  },
  {
    key: "asc",
    eyebrow: "Ambulatory surgical",
    name: "ASC",
    covTitle: "Standards in the ambulatory surgical pack",
    covCount: "4 cited",
    standards: [
      ["AAAHC pharmaceutical standards", "Chapter 9", false],
      ["DEA 21 CFR §1304", "Controlled-substance recordkeeping", false],
      ["ISMP high-alert list", "Storage and independent double-check", false],
      ["Anesthesia reconciliation", "Pre-op through discharge", false],
    ],
    note: "Assigned automatically, and ambulatory surgical facilities get five assessment worksheets.",
    tele: [
      { value: "4", label: "Standards cited" },
      { value: "5", label: "Worksheets" },
      { value: "100%", label: "Findings cited" },
    ],
    findingKicker: "What it catches",
    finding: {
      title: "Controlled-substance count variance unresolved past 24 h",
      meta: "Two consecutive shifts · no reconciliation entry logged",
      severity: "Critical",
      tone: "crit",
      cites: ["DEA 21 CFR §1304.11", "AAAHC Ch. 9"],
    },
    footLeft: "Assigned automatically · 5 assessment worksheets",
    footRight: "11 flags · 11 cited",
  },
  {
    key: "dialysis",
    eyebrow: "Renal",
    name: "Dialysis",
    covTitle: "Standards in the dialysis pack",
    covCount: "4 cited",
    standards: [
      ["42 CFR §494", "Conditions for coverage", false],
      ["AAMI water quality", "RD52 · endotoxin and chemical limits", false],
      ["KDIGO anemia", "ESA dosing and hemoglobin targets", false],
      ["KDIGO mineral & bone", "Phosphate, calcium, PTH", false],
    ],
    note: "Assigned automatically, and dialysis facilities get three assessment worksheets.",
    tele: [
      { value: "4", label: "Standards cited" },
      { value: "3", label: "Worksheets" },
      { value: "100%", label: "Findings cited" },
    ],
    findingKicker: "What it catches",
    finding: {
      title: "Water endotoxin above AAMI action level on monthly culture",
      meta: "Resample not logged within the required interval",
      severity: "Critical",
      tone: "crit",
      cites: ["AAMI RD52", "42 CFR §494.40"],
    },
    footLeft: "Assigned automatically · 3 assessment worksheets",
    footRight: "14 flags · 14 cited",
  },
  {
    key: "custom",
    eyebrow: "Anything else",
    name: "Custom",
    covTitle: "Rule packs assigned",
    covCount: "3 in catalog",
    standards: [
      ["Long-term care pack", "ASCP · Beers · STOPP/START · F-tags — assigned", true],
      ["Ambulatory surgical pack", "AAAHC · DEA · high-alert monitoring — assigned", true],
      ["Dialysis pack", "42 CFR §494 · AAMI water — available, not assigned", false],
    ],
    note: "Nothing is auto-assigned to a Custom facility. An admin assigns whichever packs apply — and no workflow family is gated.",
    tele: [
      { value: "2", label: "Packs assigned" },
      { value: "Roster", label: "Reviewed by resident" },
      { value: "100%", label: "Findings cited" },
    ],
    findingKicker: "What runs here, because you assigned it",
    finding: {
      title: "Anticholinergic burden ≥ 3 with concurrent fall risk",
      meta: "Runs here only because an admin assigned the long-term care pack",
      severity: "Critical",
      tone: "crit",
      cites: ["Beers 2023 · Table 8", "Long-term care pack"],
    },
    footLeft: "You choose which packs run here",
    footRight: "2 of 3 packs assigned",
  },
];

export function FacilityPackReadout({ dark = false }: { dark?: boolean }) {
  const reduced = useReducedMotion();
  const [at, setAt] = useState(0);
  const sweepRef = useSweep(at, reduced);
  const pack = PACKS[at] ?? PACKS[0];

  const onKeyDown = (e: React.KeyboardEvent) => {
    let next: number | null = null;
    if (e.key === "ArrowRight") next = Math.min(PACKS.length - 1, at + 1);
    else if (e.key === "ArrowLeft") next = Math.max(0, at - 1);
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = PACKS.length - 1;
    if (next === null) return;
    e.preventDefault();
    setAt(next);
    e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  };

  return (
    <div>
      <div className="pick-row" role="tablist" aria-label="Choose a facility type" onKeyDown={onKeyDown}>
        {PACKS.map((p, i) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={i === at}
            aria-controls={`pack-panel-${p.key}`}
            tabIndex={i === at ? 0 : -1}
            id={`pack-tab-${p.key}`}
            className={`pick${p.key === "custom" ? " pick-add" : ""}`}
            onClick={() => setAt(i)}
          >
            <span className="pick-eyebrow">{p.eyebrow}</span>
            <span className="pick-name">{p.name}</span>
          </button>
        ))}
      </div>

      {/* All five packs render; the inactive ones are hidden. Mounting only
          the selected one kept dialysis and Custom out of the prerendered
          HTML entirely, which is a chunk of the page's indexable copy. */}
      {PACKS.map((p, i) => (
        <div key={p.key} id={`pack-panel-${p.key}`} role="tabpanel" aria-labelledby={`pack-tab-${p.key}`} hidden={i !== at}>
          <div className="mt-8 grid grid-cols-1 items-start gap-9 lg:grid-cols-[minmax(0,0.88fr)_minmax(0,1.12fr)] lg:gap-12">
            <div>
              <div className="cov-head">
                <b>{p.covTitle}</b>
                <i>{p.covCount}</i>
              </div>
              <div className="flex flex-col">
                {p.standards.map(([name, sub, chosen]) => (
                  <div key={name} className={`cov-row${chosen ? " is-yours" : ""}`}>
                    <span className="m" />
                    <span className="n">
                      {name}
                      <small>{sub}</small>
                    </span>
                  </div>
                ))}
              </div>
              <p className="cov-note">
                <b>·</b>
                <span>{p.note}</span>
              </p>
            </div>

            <div>
              <div className={`inst${dark ? " is-dark" : ""}`}>
                <div className="inst-device">
                  <InstrumentBar title={`Rule pack · ${p.name}`}>
                    <span className="hd-live">Live</span>
                  </InstrumentBar>

                  <div className="hd-body">
                    {i === at ? <span ref={sweepRef} className="inst-sweep" aria-hidden="true" /> : null}

                    <div className={i === at ? "inst-fade" : undefined} key={pack.key}>
                      <Telemetry readouts={p.tele} hot={2} reduced={reduced} />

                      <span className="hd-k">{p.findingKicker}</span>
                      <div className="hd-find">
                        <div className="hd-find-top">
                          <p className="hd-find-t">{p.finding.title}</p>
                          <span className={`inst-sev inst-sev-${p.finding.tone}`}>
                            {p.finding.severity}
                          </span>
                        </div>
                        <p className="hd-find-m">{p.finding.meta}</p>
                      </div>
                      <div className="inst-chips" style={{ position: "relative", zIndex: 2, marginTop: "0.7rem" }}>
                        {p.finding.cites.map((c) => (
                          <span key={c} className="inst-chip">{c}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="inst-foot">
                    <span>{p.footLeft}</span>
                    <span className="ok">{p.footRight}</span>
                  </div>
                </div>
              </div>
              <p className="mt-3 text-[0.8rem] leading-relaxed text-ink-500">
                Previews are intentionally partial. The full workspace is shown in the demo.
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
